import { Group, Text, Card, Flex, ThemeIcon } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { IconLink } from "@tabler/icons-react"
import { useMemo, useCallback } from "react"

import { defaultSearchEvents, Filters } from "@costory/types/filters"
import { EventCategory, ExplorerChartType } from "@costory/types/prisma-client"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  EVENT_CATEGORIES,
  CATEGORY_MAP,
} from "@costory/front/pages/DigestV2/components/SuggestedCards/constants"
import { useEventCounts } from "@costory/front/pages/DigestV2/components/SuggestedCards/hooks/useEventCounts"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { useGithubReposQuery } from "@costory/front/queries/events"
import { useEventsQuery } from "@costory/front/queries/explore"

type SuggestedEventsCardProps = {
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  initialFormValues: Filters
  form: UseFormReturnType<Filters>
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode: boolean
  setLoading?: (loading: boolean) => void
}

export function SuggestedEventsCard({
  setChartType,
  setPage,
  initialFormValues,
  form,
}: SuggestedEventsCardProps) {
  const githubReposQuery = useGithubReposQuery()

  // Memoize search events configuration
  const suggestedSearchEvents = useMemo(
    () => ({
      ...defaultSearchEvents,
      commitments: true,
      marketplacePurchases: true,
      skuPriceChanges: true,
      reservations: true,
      githubSourceRepos:
        githubReposQuery.data?.availableRepo?.map((repo) => repo.name) || [],
    }),
    [githubReposQuery.data?.availableRepo],
  )

  const eventsQuery = useEventsQuery({
    searchForm: {
      filters: {
        from: initialFormValues.from,
        to: initialFormValues.to,
        aggBy: initialFormValues.aggBy,
      },
      searchEvents: suggestedSearchEvents,
      bookmarkedEvents: [],
    },
  })

  // Use custom hook for event counts
  const eventCounts = useEventCounts(eventsQuery)

  // Memoize click handler
  const handleClick = useCallback(
    (category?: EventCategory) => {
      form.setValues(initialFormValues)
      setChartType(ExplorerChartType.LINE)
      setPage(PageType.AdvancedExplorer)

      if (category) {
        form.setFieldValue("events", CATEGORY_MAP[category])
      }
    },
    [form, initialFormValues, setChartType, setPage],
  )

  return (
    <Card withBorder radius="md" p="md" h="100%">
      <Group gap={8} align="center" mb="sm">
        <ThemeIcon color="primaryHighlight">
          <IconLink size={20} />
        </ThemeIcon>
        <Text fw={600} size="md">
          Event Correlation
        </Text>
      </Group>
      <QueryWrapper query={eventsQuery}>
        {() => (
          <Flex direction="column" gap={6} style={{ cursor: "pointer" }}>
            {EVENT_CATEGORIES.map(
              ({ category, label, icon: IconComponent }) => (
                <Group
                  key={category}
                  gap={4}
                  align="center"
                  onClick={() => handleClick(category)}
                  data-intercom-target="costory-tour-digest-events-correlation"
                >
                  <IconComponent category={category} size={16} />
                  <Text size="md">{label} :</Text>
                  <Text size="md">
                    (
                    {
                      eventCounts[
                        category.toLowerCase() as keyof typeof eventCounts
                      ]
                    }
                    )
                  </Text>
                </Group>
              ),
            )}
          </Flex>
        )}
      </QueryWrapper>
    </Card>
  )
}
